import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import { MAX_CAROUSEL_ITEM_COUNT } from '../../OfferCarousels/common/constants'
import { sortBy, take, uniqueBy } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealAirportSelect from '../FlightDealAirportSelect'
import Group from 'components/utils/Group'
import FlightDealTileSkeletonLoader from '../FlightDealSkeletonLoader'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import FlightDealImageTile from 'components/Flights/FlightMerchandising/FlightDealImageTile'
import styled from 'styled-components'
import { rem } from 'polished'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { FlightDealsCarouselStaticFilterPills } from './FlightDealsCarouselStaticFilterPills'
import Divider from 'LuxKit/Divider'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useToggle from 'hooks/useToggle'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { FlightDealsCarouselFilterItem } from './FlightDealsCarouselFilterItem'
import { fareCabinLabelMap, FlightsClassTypes } from 'constants/flight'
import FilterChip from 'LuxKit/Chips/FilterChip'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'

const FiltersContainer = styled(Group)`
  padding: 0 ${rem(16)};
`

const StyledFlightDealTileSkeletonLoader = styled(FlightDealTileSkeletonLoader)`
  width: ${rem(300)};
`

const DividerContainer = styled.div`
  height: ${rem(16)};
  padding: 0 ${rem(4)};
`

const DividerBar = styled(Divider)`
  height: ${rem(16)};
  max-height: ${rem(16)};
`

const LookingForSomethingElseContainer = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(12)} ${rem(20)};
  width: ${rem(300)};


  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(40)};
  }
`

const Content = styled(Group)`
  text-align: center;
`

export enum FilterType {
  DESTINATIONS = 'destinations',
  AIRLINES = 'airlines',
  CABINS = 'cabins',
}

type FilterState = Record<FilterType, Set<string>>

enum FilterActionType {
  TOGGLE_FILTER = 'TOGGLE_FILTER',
  SET_FILTER_VALUE = 'SET_FILTER_VALUE',
  RESET_FILTERS = 'RESET_FILTERS',
}

type FilterAction = {
  type: FilterActionType.TOGGLE_FILTER
  payload: {
    type: FilterType
    value: string
  }
} | {
  type: FilterActionType.SET_FILTER_VALUE
  payload: {
    type: FilterType
    value: Set<string>
  }
} | {
  type: FilterActionType.RESET_FILTERS
}

export const ALL_KEY = 'all'

const initialState: FilterState = {
  destinations: new Set([ALL_KEY]),
  airlines: new Set([ALL_KEY]),
  cabins: new Set([ALL_KEY]),
}

function reducer(state: FilterState, action: FilterAction): FilterState {
  if (action.type === FilterActionType.TOGGLE_FILTER) {
    let newData = new Set([...state[action.payload.type]])

    if (action.payload.value === ALL_KEY) {
      const deleted = newData.delete(action.payload.value)
      newData = deleted ? new Set() : new Set([ALL_KEY])
    } else {
      newData.delete(ALL_KEY)
      const deleted = newData.delete(action.payload.value)
      if (!deleted) newData.add(action.payload.value)
    }

    if (newData.size === 0) {
      newData.add(ALL_KEY)
    }

    return {
      ...state,
      [action.payload.type]: newData,
    }
  }

  if (action.type === FilterActionType.SET_FILTER_VALUE) {
    return {
      ...state,
      [action.payload.type]: action.payload.value,
    }
  }

  if (action.type === FilterActionType.RESET_FILTERS) {
    return {
      ...initialState,
    }
  }

  return state
}

interface Props {
  title: string
  showViewAll?: boolean
  airports: Array<App.Airport>
  selectedAirport?: App.Airport
  defaultAirport?: App.Airport
  flightDeals: Array<App.FlightDeal>
  onAirportChange: (airport: App.Airport) => void
  loading?: boolean
  showLookingForAFlight?: boolean
  isHomepage?: boolean
}

interface FlightDealGroup {
  key: string
  primaryDeal: App.FlightDeal
  economyDeal?: App.FlightDeal
  businessDeal?: App.FlightDeal
}

function getFlightDealGroupKey(deal: App.FlightDeal): string {
  const groupKeyBase = deal.groupId || deal.id
  const baseKey = `${groupKeyBase}-${deal.originAirportCode}-${deal.destinationAirportCode}-${deal.carrier.code}-${deal.fareType}`
  const isMergeableCabin = deal.fareClass === FlightsClassTypes.ECONOMY || deal.fareClass === FlightsClassTypes.BUSINESS
  return isMergeableCabin ? baseKey : `${baseKey}-${deal.fareClass}`
}

function FlightDealsCarousel(props: Props) {
  const { title, showViewAll, airports, selectedAirport, flightDeals, defaultAirport, onAirportChange, loading, showLookingForAFlight, isHomepage = false } = props
  const intl = useIntl()
  const [filtersState, filtersDispatch] = useReducer(reducer, initialState)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const airlinesDropdownTrigger = useRef<HTMLButtonElement>(null)
  const cabinsDropdownTrigger = useRef<HTMLButtonElement>(null)
  const { value: showAirlinesFilter, toggle: toggleAirlinesFilter } = useToggle()
  const { value: showCabinsFilter, toggle: toggleCabinsFilter } = useToggle()
  const airport = selectedAirport ?? defaultAirport
  const showLookingForSomethingElse = showLookingForAFlight && filtersState.destinations.has(ALL_KEY)
  const [carouselInViewRef, isCarouselInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })
  const canTriggerMergeCabinExperiment = useMemo(() => !isHomepage || isCarouselInView, [isHomepage, isCarouselInView])
  const mergeCabinVariant = useOptimizelyExperiment(
    OptimizelyExperiments.croMergeCabinOnFlightSaleTimes,
    canTriggerMergeCabinExperiment,
  )
  const isMergeCabinEnabled = useMemo(() => getOptimizelyEnabledDecision(mergeCabinVariant ?? null), [mergeCabinVariant])

  useEffect(() => {
    carouselRef.current?.scrollTo(0, 0)
  }, [filtersState])

  const currentAirportDeals = useMemo(() => {
    if (!airport) return []

    const deals = flightDeals.filter(({ originAirportCode }) => originAirportCode === airport.code)
    return sortBy(deals, (deal) => deal.hierarchy, 'asc')
  }, [airport, flightDeals])

  useEffect(() => {
    filtersDispatch({ type: FilterActionType.RESET_FILTERS })
  }, [airport])

  const destinations = useMemo(() => {
    const countries = currentAirportDeals.filter(deal => {
      const airlineIncluded = filtersState.airlines.has(ALL_KEY) || filtersState.airlines.has(deal.carrier.code)
      const cabinIncluded = filtersState.cabins.has(ALL_KEY) || filtersState.cabins.has(deal.fareClass)
      return airlineIncluded && cabinIncluded
    }).map(deal => ({
      label: deal.destinationCountry,
      value: deal.destinationCountry,
      price: deal.perAdultPrice,
    }))

    const sortedCities = sortBy(countries, (country) => country.price, 'asc')
    return uniqueBy(sortedCities, country => country.label)
  }, [currentAirportDeals, filtersState.airlines, filtersState.cabins])

  const airlines = useMemo(() => {
    const carriers = currentAirportDeals.map(deal => ({
      label: deal.carrier.name,
      value: deal.carrier.code,
      price: deal.perAdultPrice,
    }))

    const sortedCarriers = sortBy(carriers, (carrier) => carrier.price, 'asc')
    return uniqueBy(sortedCarriers, carrier => carrier.value)
  }, [currentAirportDeals])

  const cabins = useMemo(() => {
    const data = currentAirportDeals
      .filter(deal => {
        const airlineIncluded = filtersState.airlines.has(deal.carrier.code) || filtersState.airlines.has(ALL_KEY)
        const destinationIncluded = filtersState.destinations.has(deal.destinationCountry) || filtersState.destinations.has(ALL_KEY)
        return airlineIncluded && destinationIncluded
      })
      .map(deal => ({
        label: deal.fareClass,
        value: deal.fareClass as FlightsClassTypes,
        price: deal.perAdultPrice,
      }))

    const sortedCabins = sortBy(data, (data) => data.price, 'asc')
    return uniqueBy(sortedCabins, cabin => cabin.value)
  }, [currentAirportDeals, filtersState.airlines, filtersState.destinations])

  const handleToggleFilterValue = useCallback((type: FilterType, value: string) => {
    filtersDispatch({
      type: FilterActionType.TOGGLE_FILTER,
      payload: {
        type,
        value,
      },
    })

    if (type === FilterType.AIRLINES) {
      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.CABINS,
          value: new Set([ALL_KEY]),
        },
      })

      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.DESTINATIONS,
          value: new Set([ALL_KEY]),
        },
      })
    }

    if (type === FilterType.CABINS) {
      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.DESTINATIONS,
          value: new Set([ALL_KEY]),
        },
      })
    }
  }, [])

  const filteredDeals = useMemo(() => {
    return currentAirportDeals.filter(deal => {
      let show = true

      if (filtersState.destinations.size && !filtersState.destinations.has(ALL_KEY)) {
        show = show && filtersState.destinations.has(deal.destinationCountry)
      }

      if (filtersState.airlines.size && !filtersState.airlines.has(ALL_KEY)) {
        show = show && filtersState.airlines.has(deal.carrier.code)
      }

      if (filtersState.cabins.size && !filtersState.cabins.has(ALL_KEY)) {
        show = show && filtersState.cabins.has(deal.fareClass)
      }

      return show
    })
  }, [currentAirportDeals, filtersState])

  const cheapestDestination = useMemo(() => {
    return sortBy(destinations, val => val.price, 'asc')[0]?.price
  }, [destinations])

  const groupedDeals: Array<FlightDealGroup> = useMemo(() => {
    if (!isMergeCabinEnabled) {
      return filteredDeals.map((deal) => ({
        key: deal.id,
        primaryDeal: deal,
        economyDeal: undefined,
        businessDeal: undefined,
      }))
    }

    const groupedMap = new Map<string, FlightDealGroup>()
    const orderedGroups: Array<FlightDealGroup> = []

    filteredDeals.forEach((deal) => {
      const groupKey = getFlightDealGroupKey(deal)
      const existingGroup = groupedMap.get(groupKey)
      const isEconomyDeal = deal.fareClass === FlightsClassTypes.ECONOMY
      const isBusinessDeal = deal.fareClass === FlightsClassTypes.BUSINESS

      if (!existingGroup) {
        const newGroup: FlightDealGroup = {
          key: groupKey,
          primaryDeal: deal,
          economyDeal: isEconomyDeal ? deal : undefined,
          businessDeal: isBusinessDeal ? deal : undefined,
        }

        groupedMap.set(groupKey, newGroup)
        orderedGroups.push(newGroup)
        return
      }

      if (isEconomyDeal) {
        existingGroup.economyDeal = deal
        existingGroup.primaryDeal = deal
        return
      }

      if (isBusinessDeal) {
        existingGroup.businessDeal = deal
        if (!existingGroup.economyDeal) {
          existingGroup.primaryDeal = deal
        }
      }
    })

    return orderedGroups
  }, [filteredDeals, isMergeCabinEnabled])

  const carouselDeals = useMemo(() => take(groupedDeals, MAX_CAROUSEL_ITEM_COUNT), [groupedDeals])

  const carouselDealTiles = useMemo(() => {
    return carouselDeals.map((dealGroup, index) => (
      <FlightDealImageTile
        key={dealGroup.key}
        position={index}
        flightDeal={dealGroup.primaryDeal}
        economyDeal={dealGroup.economyDeal}
        businessDeal={dealGroup.businessDeal}
        hasMultipleAirlines
      />
    ))
  }, [carouselDeals])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'FLIGHTS', 'Flights Best Flights'),
  )

  return (
    <>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <VerticalSpacer gap={32}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
              <Group direction="vertical" tabletDirection="horizontal" tabletGap={8}>
                <Heading variant="heading2">{title}</Heading>
                {airport && airports.length > 1 &&
                  <FlightDealAirportSelect
                    selected={airport}
                    airports={airports}
                    onChange={onAirportChange}
                  />}
              </Group>

              {showViewAll && <TextButton kind="tertiary" size="medium" to="/flights">
                {intl.formatMessage({
                  id: 'hAHKOz0dL+',
                  defaultMessage: 'View all',
                  description: 'Button text to view all flight deals',
                })}
              </TextButton>}
            </Group>

            {flightDeals.length > 0 && (
              <CardCarousel gap={4}>
                <FlightDealsCarouselStaticFilterPills
                  airlines={airlines}
                  cabins={cabins}
                  selectedAirlines={filtersState.airlines}
                  selectedCabins={filtersState.cabins}
                  handleToggle={handleToggleFilterValue}
                />

                <Group direction="vertical" verticalAlign="center">
                  <DividerContainer>
                    <DividerBar kind="primary" direction="vertical" />
                  </DividerContainer>
                </Group>

                {destinations.length > 0 && <FilterChip
                  size="medium"
                  onClick={() => handleToggleFilterValue(FilterType.DESTINATIONS, ALL_KEY)}
                  selected={filtersState.destinations.has(ALL_KEY)}
                >
                  <b>All destinations</b> <FormatCurrency value={cheapestDestination} />*
                </FilterChip>}

                {destinations.map(destination => (
                  <FilterChip
                    key={destination.value}
                    size="medium"
                    onClick={() => handleToggleFilterValue(FilterType.DESTINATIONS, destination.value)}
                    selected={filtersState.destinations.has(destination.label)}
                  >
                    <b>{destination.label}</b> <FormatCurrency value={destination.price} />*
                  </FilterChip>
                ))}
              </CardCarousel>
            )}
          </VerticalSpacer>
        </LayoutContainer>

        <VerticalSpacer gap={8} ref={carouselInViewRef}>
          <FullWidthCarousel
            snap="start"
            itemsPerArrow={4}
            ref={carouselRef}
          >
            {loading && <>
              <StyledFlightDealTileSkeletonLoader />
              <StyledFlightDealTileSkeletonLoader />
              <StyledFlightDealTileSkeletonLoader />
            </>}

            {!loading && <OfferListEventsProvider tracking={tracking}>
              {carouselDealTiles}

              {showLookingForSomethingElse && <LookingForSomethingElseContainer
                gap={16}
                direction="vertical"
                horizontalAlign="center"
                verticalAlign="center"
              >
                <Content direction="vertical" gap={8} verticalAlign="center">
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'y3fEb8+BBb',
                      defaultMessage: 'Looking for a flight?',
                      description: 'Heading text for flight search prompt',
                    })}
                  </BodyText>
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'k5ey1eQqGO',
                      defaultMessage: 'Search for all destinations for more amazing offers',
                      description: 'Description text encouraging users to search for all flight destinations',
                    })}
                  </BodyText>
                </Content>

                <TextButton kind="secondary" size="medium" shape="square" to="/flights">
                  {intl.formatMessage({
                    id: 'TUE6Gfk1ik',
                    defaultMessage: 'Search now',
                    description: 'Button text to start flight search',
                  })}
                </TextButton>
              </LookingForSomethingElseContainer>}
            </OfferListEventsProvider>}
          </FullWidthCarousel>

          <LayoutContainer>
            <BodyTextBlock variant="small">
              {intl.formatMessage({
                id: 'huwSv3Cnp3',
                defaultMessage: '*Starting price of the current campaigns',
                description: 'Disclaimer note about starting prices for flight deals',
              })}
            </BodyTextBlock>
          </LayoutContainer>
        </VerticalSpacer>
      </VerticalSpacer>

      <DropdownList
        size="M"
        open={showAirlinesFilter}
        triggerRef={airlinesDropdownTrigger}
        anchorRef={airlinesDropdownTrigger}
        onClose={toggleAirlinesFilter}
        placement="bottom-start"
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsCarouselFilterItem
              checked={filtersState.airlines.has(ALL_KEY)}
              onCheckedChange={() => handleToggleFilterValue(FilterType.AIRLINES, ALL_KEY)}
              label={intl.formatMessage({
                id: 'zzpmK1csMO',
                defaultMessage: 'All airlines',
                description: 'Filter label for all airlines option',
              })}
            />

            {airlines.map(airline => (
              <FlightDealsCarouselFilterItem
                key={airline.value}
                checked={filtersState.airlines.has(airline.value)}
                onCheckedChange={() => handleToggleFilterValue(FilterType.AIRLINES, airline.value)}
                label={airline.label}
                price={airline.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>

      <DropdownList
        size="M"
        open={showCabinsFilter}
        triggerRef={cabinsDropdownTrigger}
        anchorRef={cabinsDropdownTrigger}
        onClose={toggleCabinsFilter}
        placement="bottom-start"
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsCarouselFilterItem
              checked={filtersState.cabins.has(ALL_KEY)}
              onCheckedChange={() => handleToggleFilterValue(FilterType.CABINS, ALL_KEY)}
              label={intl.formatMessage({
                id: 'tZLX+QSxqW',
                defaultMessage: 'All cabins',
                description: 'Filter label for all cabins option',
              })}
            />

            {cabins.map(cabin => (
              <FlightDealsCarouselFilterItem
                key={cabin.value}
                checked={filtersState.cabins.has(cabin.value)}
                onCheckedChange={() => handleToggleFilterValue(FilterType.CABINS, cabin.value)}
                label={fareCabinLabelMap[cabin.value]}
                price={cabin.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>
    </>
  )
}

export default FlightDealsCarousel
